import React, { useContext, useEffect, useRef, useState } from "react";
import { FormContext } from "../form_buttons";
import { Box, FormControl, TextField } from "@mui/material";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import { fieldMarginBottom } from "../../form_constants";
import dayjs from "dayjs";

interface FormInputProp {
    field: string;
    type?: "text" | "number" | "date" | "datetime" | "password" | "email";
    label: string;
    rows?: number;
    disabled?: boolean;
    onChange?: (value: any) => void;
    onKeyUp?: (value: any) => void;
    inputProps?: object;
    helperText?: string;
    placeholder?: string;
    value?: any;
}

export const FormInput = ({
    field,
    type = "text",
    label,
    rows,
    disabled,
    onChange,
    onKeyUp,
    inputProps,
    helperText,
    placeholder,
    value,
}: FormInputProp) => {
    const formContext = useContext(FormContext);
    const form = formContext.form;
    const modelField = form.field(field);

    const initialValue: string = value
        ? value
        : type.indexOf("date") < 0
          ? (modelField.value() ?? "")
          : (modelField.valueAsDate() ?? "");

    const [modelValue, setModelValue] = useState(initialValue);
    useEffect(() => { setModelValue(initialValue ?? ""); }, [initialValue]);

    // Ref para que el getter registrado lea siempre el valor actual sin closure obsoleta.
    const modelValueRef = useRef(modelValue);
    modelValueRef.current = modelValue;

    useEffect(() => {
        form.register(field, () => modelValueRef.current, t => setModelValue(t ?? ""));
        return () => form.unregister(field);
    }, [field]);

    const handleTextChange = (newValue: string) => {
        setModelValue(newValue);
        form.notifyFieldChange(field, newValue);
        onChange?.(newValue);
    };

    return (
        <>
            {type !== "date" && type !== "datetime" && (
                <Box className={["form-input", "form-input-" + field].join(" ")} mb={fieldMarginBottom}>
                    <TextField
                        id={field}
                        variant="filled"
                        label={label}
                        type={type}
                        rows={rows || 1}
                        multiline={rows ? rows > 1 : undefined}
                        fullWidth
                        autoComplete="off"
                        disabled={disabled}
                        value={modelValue}
                        inputProps={inputProps}
                        onKeyUp={onKeyUp}
                        helperText={helperText}
                        placeholder={placeholder}
                        onChange={e => handleTextChange(e.target.value)}
                    />
                </Box>
            )}
            {type === "date" && (
                <Box className={["form-input", "form-input-" + field].join(" ")} mb={1}>
                    <FormControl fullWidth>
                        <DatePicker
                            disabled={disabled}
                            value={modelValue ? dayjs(modelValue) : null}
                            format="DD/MM/YYYY"
                            label={label}
                            slotProps={{ textField: { id: field, variant: "filled", fullWidth: true, helperText } }}
                            onChange={val => {
                                const strValue = val ? dayjs(val).format("YYYY-MM-DD") : "";
                                setModelValue(strValue);
                                form.notifyFieldChange(field, strValue);
                                onChange?.(val);
                            }}
                        />
                    </FormControl>
                </Box>
            )}
            {type === "datetime" && (
                <Box className={["form-input", "form-input-" + field].join(" ")} mb={1}>
                    <FormControl fullWidth>
                        <DateTimePicker
                            disabled={disabled}
                            value={modelValue ? dayjs(modelValue) : null}
                            label={label}
                            slotProps={{ textField: { id: field, variant: "filled", fullWidth: true } }}
                            onChange={val => {
                                const strValue = val ? dayjs(val).format("YYYY-MM-DDTHH:mm:ss") : "";
                                setModelValue(strValue);
                                form.notifyFieldChange(field, strValue);
                                onChange?.(val);
                            }}
                        />
                    </FormControl>
                </Box>
            )}
        </>
    );
};
